import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useSession } from '../auth/session'
import { Permissions } from '../auth/permissions'
import { visibleNavigation } from './navigation'
import { sectionsByModule } from './sections'

type Hit = { group: string; label: string; detail?: string; to: string }
type Named = { id: string; code: string; name: string; phone?: string }
type Account = { code: string; name: string; isActive: boolean }

const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

/**
 * One box for finding anything this person may open: a page, a customer, a product, a
 * ledger account. Ctrl K (⌘K on a Mac) reaches it from anywhere; the arrow keys and
 * Enter go.
 */
export function GlobalSearch() {
  const navigate = useNavigate()
  const can = useSession((s) => s.can)
  const [typed, setTyped] = useState('')
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setTerm(typed.trim()), 200)
    return () => window.clearTimeout(timer)
  }, [typed])

  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        input.current?.focus()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', shortcut)
    return () => window.removeEventListener('keydown', shortcut)
  }, [])

  useEffect(() => {
    if (!open) return
    const outside = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', outside)
    return () => document.removeEventListener('pointerdown', outside)
  }, [open])

  const q = term.length >= 2 ? term : ''
  const lower = q.toLowerCase()
  const customers = useQuery({
    queryKey: ['search', 'customers', q],
    queryFn: ({ signal }) => api<{ items: Named[] }>(`/api/customers?search=${encodeURIComponent(q)}&page=1`, { signal }),
    enabled: !!q && can(Permissions.Customer.View),
    staleTime: 30_000,
  })
  const products = useQuery({
    queryKey: ['search', 'products', q],
    queryFn: ({ signal }) => api<{ items: Named[] }>(`/api/products?search=${encodeURIComponent(q)}&status=active&page=1&pageSize=5`, { signal }),
    enabled: !!q && can(Permissions.Product.View),
    staleTime: 30_000,
  })
  const accounts = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: ({ signal }) => api<Account[]>('/api/finance/accounts', { signal }),
    enabled: !!q && can(Permissions.Finance.ViewAccounts),
  })

  const hits = useMemo<Hit[]>(() => {
    if (!q) return []
    const pages = visibleNavigation(can).flatMap((item): Hit[] => [
      { group: 'Pages', label: item.label, to: item.to },
      ...(sectionsByModule[item.icon]?.(can) ?? []).map((p) => ({ group: 'Pages', label: p.label, detail: item.label, to: p.to })),
    ]).filter((p) => p.label.toLowerCase().includes(lower) || p.detail?.toLowerCase().includes(lower))
    const seen = new Set<string>()
    return [
      ...pages.filter((p) => !seen.has(p.to) && seen.add(p.to)).slice(0, 5),
      ...(customers.data?.items ?? []).slice(0, 5).map((c) => ({ group: 'Customers', label: c.name, detail: [c.code, c.phone].filter(Boolean).join(' · '), to: `/customers/${c.id}` })),
      ...(products.data?.items ?? []).slice(0, 5).map((p) => ({ group: 'Products', label: p.name, detail: p.code, to: `/products/${p.id}` })),
      ...(accounts.data ?? []).filter((a) => a.code.startsWith(q) || a.name.toLowerCase().includes(lower)).slice(0, 5)
        .map((a) => ({ group: 'Accounts', label: `${a.code} ${a.name}`, detail: a.isActive ? undefined : 'inactive', to: `/finance/accounts/${a.code}` })),
    ]
  }, [q, lower, can, customers.data, products.data, accounts.data])

  const loading = !!q && (customers.isFetching || products.isFetching)
  const current = open ? hits[Math.min(active, hits.length - 1)] : undefined

  function go(hit: Hit) {
    navigate(hit.to)
    setTyped('')
    setTerm('')
    setOpen(false)
    input.current?.blur()
  }

  return (
    <div ref={box} className="relative hidden md:block">
      <label className="flex w-72 items-center gap-2 rounded-lg border border-dpl-border bg-dpl-bg px-3 focus-within:border-dpl-primary focus-within:ring-1 focus-within:ring-dpl-primary lg:w-96">
        <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 text-dpl-muted" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
          <circle cx="8.5" cy="8.5" r="5.5" />
          <path d="m13 13 4.5 4.5" />
        </svg>
        <input
          ref={input}
          type="search"
          role="combobox"
          aria-label="Global search"
          aria-expanded={open && !!q}
          aria-controls="global-search-results"
          aria-activedescendant={current ? `global-hit-${hits.indexOf(current)}` : undefined}
          placeholder="Search pages, customers, products…"
          className="h-10 min-w-0 flex-1 bg-transparent text-sm text-dpl-text placeholder:text-dpl-muted focus:outline-none"
          value={typed}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setTyped(event.target.value)
            setActive(0)
            setOpen(true)
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              setActive((a) => Math.min(a + 1, hits.length - 1))
            } else if (event.key === 'ArrowUp') {
              event.preventDefault()
              setActive((a) => Math.max(a - 1, 0))
            } else if (event.key === 'Enter') {
              event.preventDefault()
              if (current) go(current)
            } else if (event.key === 'Escape') {
              setOpen(false)
              input.current?.blur()
            }
          }}
        />
        <kbd className="hidden shrink-0 rounded border border-dpl-border bg-dpl-surface px-1.5 py-0.5 text-[11px] font-medium text-dpl-muted lg:inline">{mac ? '⌘K' : 'Ctrl K'}</kbd>
      </label>
      {open && q && (
        <ul id="global-search-results" role="listbox" aria-label="Search results" className="absolute right-0 z-40 mt-2 max-h-[70vh] w-full min-w-[22rem] overflow-auto rounded-lg border border-dpl-border bg-dpl-surface py-1.5 shadow-lg">
          {hits.length === 0 && <li className="px-4 py-3 text-sm text-dpl-muted">{loading ? 'Searching…' : `Nothing matches “${q}”.`}</li>}
          {hits.map((hit, index) => (
            <li
              key={`${hit.group}-${hit.to}`}
              id={`global-hit-${index}`}
              role="option"
              aria-selected={hit === current}
              onPointerDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => go(hit)}
              className={`cursor-pointer px-4 py-2 ${hit === current ? 'bg-dpl-primary/10' : ''}`}
            >
              {(index === 0 || hits[index - 1].group !== hit.group) && (
                <span className={`-mx-4 mb-1 block px-4 text-[11px] font-semibold uppercase tracking-wide text-dpl-muted ${index > 0 ? 'mt-1 border-t border-dpl-border pt-2' : ''}`} aria-hidden="true">{hit.group}</span>
              )}
              <span className="block truncate text-sm font-medium">{hit.label}</span>
              {hit.detail && <span className="block truncate text-xs text-dpl-muted">{hit.detail}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
